import { useState } from "react"
import { Link } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FuseguLogo } from "@/components/FuseguLogo"
import { ArrowLeft, Mail, CheckCircle } from "lucide-react"

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setIsLoading(true)

    try {
      const response = await fetch("https://api.fusegusecurity.com/api/auth/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess(true)
      } else {
        setError(data.error || "Failed to send reset email")
      }
    } catch (err) {
      setError("Network error. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Forgot Password | Fusegu Security</title>
        <meta name="description" content="Reset your Fusegu account password" />
      </Helmet>

      <div className="flex min-h-screen w-full flex-col">
        <h1 className="sr-only">Reset Your Fusegu Password</h1>
        {/* Header */}
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
          <Link to="/" className="flex items-center gap-2">
            <FuseguLogo className="h-6 w-6 flex-shrink-0" />
            <span className="text-lg font-bold">Fusegu</span>
          </Link>
        </header>

        <main className="flex flex-1 items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="w-full max-w-md">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to login
            </Link>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-center mb-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <CardTitle className="text-center">Forgot Password?</CardTitle>
                <CardDescription className="text-center">
                  {success
                    ? "Check your email for reset instructions"
                    : "Enter your email and we'll send you a reset link"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {success ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center">
                      <div className="p-3 bg-green-100 rounded-full">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                    </div>
                    <div className="text-center space-y-2">
                      <p className="text-sm text-gray-700">
                        If an account exists with <strong>{email}</strong>, you will
                        receive a password reset link shortly.
                      </p>
                      <p className="text-xs text-gray-500">
                        The link will expire in 1 hour for security reasons.
                      </p>
                    </div>
                    <div className="pt-4 text-center">
                      <p className="text-sm text-gray-600 mb-4">
                        Didn't receive the email? Check your spam folder.
                      </p>
                      <Link to="/login">
                        <Button variant="outline" className="w-full">
                          Return to Login
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    {error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading || !email}
                    >
                      {isLoading ? "Sending..." : "Send Reset Link"}
                    </Button>

                    <div className="text-center text-sm text-gray-600">
                      Remember your password?{" "}
                      <Link to="/login" className="text-blue-600 hover:underline">
                        Sign in
                      </Link>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </>
  )
}
